import type { WorkspaceKpi } from './command-workspace-contract';
import type { MonitoringReadState } from './polling';

export interface AutonomousSummary {
  instanceId: 'forest-demo-1';
  areaId: string | null;
  scope: 'CURRENT_AUTHORIZED_INSTANCE';
  mode: 'SYNTHETIC_EXERCISE';
  registryBackend: 'sqlite' | 'mysql';
  asOf: string;
  configRevision: number;
  runner: {
    status: 'RUNNING' | 'STOPPED' | 'UNAVAILABLE';
    lastHeartbeatAt: string | null;
    lastAcceptedAt: string | null;
    lastProcessedAt: string | null;
    error: string | null;
  };
  coverage: {
    configuredAreas: number;
    enabledSources: number;
    eligibleSources: number;
    healthySources: number;
    unknownSources: number;
    unlocatedSources: number;
  };
  observedWarningCount: number | null;
  predictiveAttentionCount: number | null;
  pendingEvaluationCount: number | null;
  dataStatus: 'READY' | 'STALE' | 'NO_DATA' | 'UNAVAILABLE';
  sources: {
    sourceId: string;
    areaId: string;
    epoch: number;
    health: 'ACTIVE_HEALTHY' | 'STALE' | 'NO_DATA' | 'DISABLED' | 'UNAVAILABLE';
    locationStatus: 'UNLOCATED';
    dataOrigin: 'SYNTHETIC_EXERCISE';
    latestObservedAt: string | null;
    latestReceivedAt: string | null;
  }[];
}
export interface AutonomousPort {
  read(areaId: string, signal: AbortSignal): Promise<AutonomousSummary>;
}
/** Both Page01 and the risk workspace use the server's same scope/time projection.
 * A successful read is not evidence of qualified observations or a healthy worker. */
export function autonomousKpis(state: MonitoringReadState<AutonomousSummary>): WorkspaceKpi[] {
  const data = state.status === 'READY' ? state.data : null;
  const usable = data?.dataStatus === 'READY' && data.runner.status === 'RUNNING';
  const scope = data ? `${data.areaId ?? '全部授权区域'} · ${data.asOf}` : '等待后端同源摘要';
  return [
    {
      key: 'warnings',
      label: '观测预警',
      value: usable ? data.observedWarningCount : null,
      note: `${scope} · 既有规则的内部合成预警；不是正式外部预警`,
    },
    {
      key: 'forecasts',
      label: '预测关注',
      value: usable ? data.predictiveAttentionCount : null,
      note: `${scope} · 独立实验预测，NEEDS_IMPROVEMENT`,
    },
    {
      key: 'coverage',
      label: '来源覆盖',
      value:
        data && data.dataStatus !== 'UNAVAILABLE'
          ? `${data.coverage.healthySources} / ${data.coverage.enabledSources}`
          : null,
      note: `${scope} · 健康来源 / 启用来源；不表示区域安全`,
    },
    {
      key: 'pending',
      label: '待核对',
      value: usable ? data.pendingEvaluationCount : null,
      note: `${scope} · 待评价预测，不是救援任务`,
    },
  ];
}
